import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { RiSignalTowerLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import ProgressBar from '@/components/ui/ProgressBar';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const INITIAL_PARAMS = {
  Copter: {
    FS_THR_ENABLE: null,
    FS_THR_VALUE: null,
  },
  Plane: {
    THR_FAILSAFE: null,
    THR_FS_VALUE: null,
    FS_SHORT_ACTN: null,
    FS_SHORT_TIMEOUT: null,
    FS_LONG_ACTN: null,
    FS_LONG_TIMEOUT: null,
  },
};

const PARAM_IDS = {
  Copter: { enable: 'FS_THR_ENABLE', pwm: 'FS_THR_VALUE' },
  Plane: { enable: 'THR_FAILSAFE', pwm: 'THR_FS_VALUE' },
};

const Panel = ({ data: [robot] }) => {
  const dispatch = useDispatch();
  const robotParams = useSelector((state) => state.robotParams[robot.id]);
  const [params, setParams] = useState(INITIAL_PARAMS[robot.model.vehicle]);
  const [isProcessing, setProcessing] = useState(false);

  const { getAddress } = useMavLinkAddress();
  const paramCommand = useParamCommand();

  const { enable: enableParamId, pwm: pwmParamId } = PARAM_IDS[robot.model.vehicle];

  // 수신된 파라미터 갯수
  const isLoadedCount = useMemo(() => {
    return Object.values(params).filter((value) => value !== null).length;
  }, [params]);

  // 파라미터 수신 중 여부
  const isLoading = useMemo(() => {
    return isLoadedCount < Object.keys(params).length;
  }, [isLoadedCount]);

  // Failsafe Action 활성화 여부
  const isEnabled = useMemo(() => {
    return params[enableParamId]?.value !== 0;
  }, [params[enableParamId]?.value]);

  useMountEffect(() => {
    // 이미 저장된 파라미터가 있으면 먼저 적용
    setParams((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(prev)) {
        const stored = robotParams.params[key];
        if (stored) {
          next[key] = { value: stored.value, type: stored.type };
        }
      }
      return next;
    });

    const address = getAddress(robot.id);
    const paramsGroup = Object.keys(params).map((key) => [...address, key, -1]);

    paramCommand.read(robot, paramsGroup);
  });

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (!Object.keys(params).includes(lastParamId)) return;

    setParams((prev) => ({
      ...prev,
      [lastParamId]: {
        value: robotParams.params[lastParamId].value,
        type: robotParams.params[lastParamId].type,
      },
    }));
  }, [robotParams]);

  const handleChange = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = Number(e.target.value);

    setParams(nextParams);
  };

  //#region Process Functions
  const process = async () => {
    try {
      const paramSet = Object.entries(params)
        .filter(([key]) => (isEnabled ? true : key === enableParamId))
        .map(([id, { value, type }]) => ({ id, value, type }));

      for (const param of paramSet) {
        await executeMavLinkCommand(robot, paramCommand.set(param.id, param.value, param.type));
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    notifier.success(`Failsafe settings saved successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to save failsafe settings on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    process();
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Configure radio failsafe settings</div>
      {isLoading && <ProgressBar current={isLoadedCount} total={Object.keys(params).length} />}
      {!isLoading && (
        <>
          <div className={cx('form')}>
            <LabelGroup htmlFor={enableParamId} label="Throttle Failsafe">
              <Select
                id={enableParamId}
                name={enableParamId}
                value={params[enableParamId].value}
                onChange={handleChange}>
                <option value={0}>Disabled</option>
                {robot.model.vehicle === 'Copter' && <option value={3}>Land</option>}
                {robot.model.vehicle === 'Copter' && <option value={1}>RTL</option>}
                {robot.model.vehicle === 'Plane' && <option value={1}>Enabled</option>}
              </Select>
            </LabelGroup>
            {isEnabled && (
              <>
                <LabelGroup htmlFor={pwmParamId} label="PWM Threshold">
                  <div className={cx('fieldWrapper')}>
                    <FieldNumber
                      id={pwmParamId}
                      name={pwmParamId}
                      value={params[pwmParamId].value}
                      onChange={handleChange}
                    />
                  </div>
                </LabelGroup>
                {robot.model.vehicle === 'Plane' && (
                  <>
                    <LabelGroup htmlFor="FS_SHORT_ACTN" label="Short Action">
                      <Select
                        id="FS_SHORT_ACTN"
                        name="FS_SHORT_ACTN"
                        value={params['FS_SHORT_ACTN'].value}
                        align="right"
                        onChange={handleChange}>
                        <option value={0}>Circle or Continue (in AUTO | GUIDED | LOITER)</option>
                        <option value={1}>Circle</option>
                      </Select>
                    </LabelGroup>
                    <LabelGroup htmlFor="FS_SHORT_TIMEOUT" label="Short Timer">
                      <div className={cx('fieldWrapper')}>
                        <FieldNumber
                          id="FS_SHORT_TIMEOUT"
                          name="FS_SHORT_TIMEOUT"
                          value={params['FS_SHORT_TIMEOUT'].value}
                          onChange={handleChange}
                        />
                        <span className={cx('unit')}>sec</span>
                      </div>
                    </LabelGroup>
                    <LabelGroup htmlFor="FS_LONG_ACTN" label="Long Action">
                      <Select
                        id="FS_LONG_ACTN"
                        name="FS_LONG_ACTN"
                        value={params['FS_LONG_ACTN'].value}
                        align="right"
                        onChange={handleChange}>
                        <option value={0}>RTL or Continue (in AUTO | GUIDED | LOITER)</option>
                        <option value={1}>RTL</option>
                      </Select>
                    </LabelGroup>
                    <LabelGroup htmlFor="FS_LONG_TIMEOUT" label="Long Timer">
                      <div className={cx('fieldWrapper')}>
                        <FieldNumber
                          id="FS_LONG_TIMEOUT"
                          name="FS_LONG_TIMEOUT"
                          value={params['FS_LONG_TIMEOUT'].value}
                          onChange={handleChange}
                        />
                        <span className={cx('unit')}>sec</span>
                      </div>
                    </LabelGroup>
                  </>
                )}
              </>
            )}
          </div>
          <ConfirmSlider
            width="100%"
            height={40}
            onConfirm={doCommand}
            isProcessing={isProcessing}
            labelStyle={{ fontSize: 12 }}
          />
        </>
      )}
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Radio';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiSignalTowerLine;
Command.Panel = Panel;

export default Command;
